/**
 * authService.js — Authentication API calls (JWT-based).
 *
 * All auth endpoints go through /api/tenant/auth/*
 */

const AUTH_BASE = '/api/tenant/auth';
const FORM_URLENCODED = 'application/x-www-form-urlencoded';

/**
 * Login with username + password (+ optional workspace/org slug) → JWT token.
 *
 * When ``organization`` is supplied the backend will scope the token to
 * that org. Without it, the token defaults to the user's primary org.
 */
export async function loginUser(username, password, organization) {
  const formData = new URLSearchParams();
  formData.append('username', username);
  formData.append('password', password);
  if (organization) {formData.append('organization', organization);}

  const response = await fetch(`${AUTH_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': FORM_URLENCODED },
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const detail = data.detail || 'Login failed';
    const error = new Error(detail);
    error.status = response.status;
    error.detail = detail;
    throw error;
  }

  return response.json();
}

/**
 * Register a new user
 */
export async function registerUser({ email, username, password, full_name, timezone }) {
  const response = await fetch(`${AUTH_BASE}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, username, password, full_name, timezone }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Registration failed');
  }

  return response.json();
}

/**
 * Get current user profile (validates token).
 *
 * The thrown Error always carries a numeric ``status`` field so callers
 * can distinguish a definitive auth failure (401) from a transient
 * problem (network down, aborted fetch when the page is navigating, 5xx
 * blip). Treating an aborted fetch as "token invalid" is what caused
 * deep-linked authenticated routes to clobber localStorage mid-redirect.
 */
export async function getCurrentUser(token) {
  let response;
  try {
    response = await fetch(`${AUTH_BASE}/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (e) {
    // Network failure or aborted fetch (e.g. page is navigating away).
    // Not a definitive auth failure — surface as status 0 so the caller
    // can keep the existing optimistic auth state intact.
    const err = new Error(`Network error contacting /auth/me: ${e.message}`);
    err.status = 0;
    throw err;
  }

  if (!response.ok) {
    const err = new Error(`/auth/me returned ${response.status}`);
    err.status = response.status;
    throw err;
  }

  return response.json();
}

/**
 * Update current user's profile
 */
export async function updateProfile(token, { full_name, timezone }) {
  const formData = new URLSearchParams();
  if (full_name != null) {formData.append('full_name', full_name);}
  if (timezone != null) {formData.append('timezone', timezone);}

  const response = await fetch(`${AUTH_BASE}/me`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': FORM_URLENCODED,
    },
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Update failed');
  }

  return response.json();
}

/**
 * Get public validation policy (for signup form)
 */
/**
 * Swap the HttpOnly refresh cookie for a fresh access token.
 *
 * The cookie rotates on every call, so this must not be issued concurrently
 * with itself; AuthContext drives it from a single timer. Returns null when
 * the session is gone, letting the caller fall back to the expiry flow.
 */
export async function refreshSession() {
  try {
    const response = await fetch(`${AUTH_BASE}/refresh`, {
      method: 'POST',
      credentials: 'same-origin',
    });
    if (!response.ok) {return null;}
    return await response.json();
  } catch {
    return null;
  }
}

/** Revoke the refresh-token family server-side (best effort). */
export async function logoutSession() {
  try {
    await fetch(`${AUTH_BASE}/logout`, { method: 'POST', credentials: 'same-origin' });
  } catch {
    /* logging out locally is what matters */
  }
}

// Single anonymous bootstrap read, shared by the helpers below. Cached for the
// page's lifetime: /deployment/config is static per deployment, and the login
// screen would otherwise hit it once per consumer.
let _deploymentConfig = null;
async function fetchDeploymentConfig() {
  if (_deploymentConfig) {return _deploymentConfig;}
  try {
    const response = await fetch(`${AUTH_BASE.replace('/auth', '')}/deployment/config`);
    if (!response.ok) {return null;}
    _deploymentConfig = await response.json();
    return _deploymentConfig;
  } catch {
    return null;
  }
}

// Password rules for the signup form. Served by /deployment/config since
// 2026-08-19 (the standalone /api/validation-policy/public was folded in) so
// the login screen makes one anonymous request instead of three.
export async function getValidationPolicy() {
  const cfg = await fetchDeploymentConfig();
  return cfg?.password_policy ?? null;
}

/**
 * Check whether Microsoft Entra SSO is enabled on the backend.
 * Returns { enabled: boolean }.
 */
export async function getEntraSsoConfig() {
  const cfg = await fetchDeploymentConfig();
  return cfg?.entra ?? { enabled: false };
}

/**
 * Start the Microsoft Entra SSO flow by navigating the browser to the
 * backend /auth/entra/login endpoint, which will 302 to Microsoft.
 * `nextPath` is preserved through the round-trip so we land back on the
 * original page after sign-in.
 */
export function startEntraLogin(nextPath = '/') {
  const url = `${AUTH_BASE}/entra/login?next=${encodeURIComponent(nextPath)}`;
  window.location.href = url;
}

/**
 * Resend email verification
 */
export async function resendVerification(username) {
  const formData = new URLSearchParams();
  formData.append('username', username);

  const response = await fetch(`${AUTH_BASE}/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': FORM_URLENCODED },
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to resend verification');
  }

  return response.json();
}

export async function verifyEmailToken(token) {
  const response = await fetch(`${AUTH_BASE}/verify-email/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Verification failed');
  }

  return response.json();
}

export default {
  loginUser,
  registerUser,
  getCurrentUser,
  updateProfile,
  getValidationPolicy,
  resendVerification,
  verifyEmailToken,
  getEntraSsoConfig,
  startEntraLogin,
};
